import React, {useEffect, useState, useMemo} from 'react';
import {Props} from './ac-search-input.proptypes';
import clsx from 'clsx';

// Imports => Constants
import {DELAYS, ICONS, TYPES} from '@asdf-constants';

// Imports => Utilities
import {AcUUID, AcIsSet, AcIsEmptyString} from '@asdf-utils';

// Imports => Atoms
import {AcTextInput} from '@asdf-atoms/ac-text-input';
import {AcIcon} from '@asdf-atoms/ac-icon';

const _CLASSES = {
  MAIN: 'ac-search-input',
  FOCUS: 'ac-search-input--focus',
  EMPTY: 'ac-search-input--empty',
  INPUT: 'ac-search-input__field',
  ICON: 'ac-search-input__icon',
};

let _delay = null;

const AcSearchInput = ({
  id = AcUUID(),
  type = TYPES.TEXT,
  value = '',
  name = 'query',
  placeholder = 'Search',
  disabled = false,
  callback,
  onClear,
  onFocus,
  onBlur,
  onKeyUp,
  validation,
  className,
  additional = {},
}) => {
  const [focus, setFocus] = useState(false);
  const [query, setQuery] = useState(value || '');

  useEffect(() => {
    if (AcIsSet(value) && value === '') {
      setQuery(value);
    }
  }, [value]);

  const handleFocus = event => {
    setFocus(true);
    if (AcIsSet(onFocus)) onFocus(event);
  };

  const handleBlur = event => {
    setFocus(false);
    if (AcIsSet(onBlur)) onBlur(event);
  };

  const handleSubmit = event => {
    if (event && event.persist) event.persist();
    if (event && event.stopPropagation) event.stopPropagation();
    if (event && event.preventDefault) event.preventDefault();
  };

  const handleKeyUp = event => {
    const key = event.key || event.which;
    const $query = document.querySelector('input[name="query"]');

    if ($query !== document.activeElement) return;
    if (!key) return;

    switch (key) {
      case 'Escape':
      case 27:
        clearQuery(event);
        break;

      default:
    }

    if (AcIsSet(onKeyUp)) onKeyUp(event);
  };

  console.debug("DEBUGPRINT[5]: ac-search-input.web.tsx:90: value=", value)
  const handleCallback = (event, name, value, wait = null) => {
    if (_delay) clearTimeout(_delay);

    _delay = setTimeout(() => {
      console.debug('DEBUGPRINT[5]: ac-search-input.web.tsx:90: value=', value);
      setQuery(value);
      if (AcIsSet(value) && AcIsSet(callback)) callback(event, name, value.trim());
    }, 0);
  };

  const clearQuery = event => {
    if (event && event.persist) event.persist();
    if (event && event.preventDefault) event.preventDefault();
    if (event && event.stopPropagation) event.stopPropagation();

    handleCallback(event, name, '', 0);
    if (AcIsSet(onClear)) onClear(event);

    const $input = document.querySelector(`[id="${id}"]`);
    if (AcIsSet($input)) $input.value = '';
  };

  const getClearQueryIconClassNames = useMemo(() => {
    return clsx(_CLASSES.ICON);
  }, []);

  const getSearchInputClassNames = useMemo(() => {
    return clsx(_CLASSES.INPUT);
  }, []);

  const getMainClassNames = useMemo(() => {
    return clsx(
      _CLASSES.MAIN,
      focus && _CLASSES.FOCUS,
      !(AcIsSet(query) && query !== '') && _CLASSES.EMPTY,
      className
    );
  }, [focus, query, className]);

  const getSearchInputOptions = useMemo(() => {
    return {
      id,
      type,
      placeholder,
      name,
      value,
      disabled,
      validation,
      additional,
      required: true,
      autocomplete: 'off',
      callback: handleCallback,
      onKeyUp: handleKeyUp,
      onFocus: handleFocus,
      onBlur: handleBlur,
      suffix:
        AcIsSet(value) && !AcIsEmptyString(value) ? (
          <AcIcon
            icon={ICONS.CLOSE}
            callback={clearQuery}
            className={getClearQueryIconClassNames}
            title={'Clear search'}
          />
        ) : null,
      className: getSearchInputClassNames,
    };
  }, [
    id,
    type,
    placeholder,
    name,
    value,
    disabled,
    validation,
    callback,
    additional,
    clearQuery,
    getSearchInputClassNames,
    getClearQueryIconClassNames,
  ]);

  return (
    <div className={getMainClassNames}>
      <form autoComplete={'off'} onSubmit={handleSubmit}>
        <AcTextInput {...getSearchInputOptions} />
      </form>
    </div>
  );
};

AcSearchInput.displayName = 'AcSearchInput';
AcSearchInput.propTypes = Props;

export {AcSearchInput};
export default AcSearchInput;
